import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { X, Plus, Trash2, Palette, Check } from 'lucide-react';
import { useTheme, buildCustomTheme } from '../contexts/ThemeContext';
import { toast } from '../hooks/use-toast';

const EMOJI_OPTIONS = ['🎨', '🌙', '💎', '🔮', '🌸', '⚡', '🍀', '🎵', '❄️', '🌈', '🪐', '💫'];

const ColorPicker = ({ label, value, onChange }) => (
  <div className="flex items-center gap-3">
    <label className="text-white/70 text-xs w-24 flex-shrink-0">{label}</label>
    <div className="relative">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-10 h-10 rounded-lg cursor-pointer border-2 border-white/20 bg-transparent"
      />
    </div>
    <span className="text-white/50 text-xs font-mono">{value}</span>
  </div>
);

const ThemeBuilderModal = ({ onClose, editTheme = null }) => {
  const { addCustomTheme, updateCustomTheme, deleteCustomTheme, setThemeId } = useTheme();

  const [name, setName] = useState(editTheme?.name || '');
  const [icon, setIcon] = useState(editTheme?.icon || '🎨');
  const [gradientStart, setGradientStart] = useState(editTheme?.colors?.gradientStart || '#1a0a2e');
  const [gradientMid, setGradientMid] = useState(editTheme?.colors?.gradientMid || '#16213e');
  const [gradientEnd, setGradientEnd] = useState(editTheme?.colors?.gradientEnd || '#0f3460');
  const [accent, setAccent] = useState(editTheme?.colors?.accent || '#e94560');

  const isEditing = !!editTheme;

  const preview = buildCustomTheme({
    id: editTheme?.id || 'preview',
    name: name || 'Preview',
    icon,
    gradientStart,
    gradientMid,
    gradientEnd,
    accent,
  });

  const handleSave = () => {
    if (!name.trim()) {
      toast({ title: 'Name required', description: 'Give your theme a name.', variant: 'destructive' });
      return;
    }

    const themeData = {
      id: editTheme?.id || `custom_${Date.now()}`,
      name: name.trim(),
      icon,
      gradientStart,
      gradientMid,
      gradientEnd,
      accent,
    };

    if (isEditing) {
      updateCustomTheme(themeData);
      toast({ title: 'Theme Updated', description: `"${name}" has been updated.` });
    } else {
      addCustomTheme(themeData);
      toast({ title: 'Theme Created', description: `"${name}" is now in your theme rotation.` });
    }
    onClose();
  };

  const handleDelete = () => {
    if (editTheme) {
      deleteCustomTheme(editTheme.id);
      toast({ title: 'Theme Deleted', description: `"${editTheme.name}" has been removed.` });
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="theme-builder-modal">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-slate-900 to-slate-800 border-white/20">
        <CardHeader className="relative">
          <Button onClick={onClose} variant="ghost" size="sm" className="absolute top-4 right-4 text-white hover:bg-white/10" data-testid="theme-builder-close">
            <X className="w-5 h-5" />
          </Button>
          <CardTitle className="text-white text-xl flex items-center gap-2">
            <Palette className="w-5 h-5 text-purple-400" />
            {isEditing ? 'Edit Theme' : 'Create Custom Theme'}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Live Preview */}
          <div className="rounded-xl overflow-hidden border border-white/20" data-testid="theme-preview">
            <div
              className="p-6"
              style={preview.bgStyle}
            >
              <h3
                className="text-2xl font-bold mb-1"
                style={preview.headerGradientStyle}
              >
                {icon} {name || 'My Theme'}
              </h3>
              <p className="text-white/60 text-sm mb-4">Live preview of your theme</p>
              <div className="flex gap-3">
                <div
                  className="bg-white/10 backdrop-blur-md rounded-lg p-3 flex-1"
                  style={{ borderWidth: '1px', ...preview.cardBorderStyle }}
                >
                  <div className="text-white text-sm font-medium">Card</div>
                  <div className="text-white/60 text-xs">With accent border</div>
                </div>
                <button
                  className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                  style={preview.accentBtnStyle}
                >
                  Accent Button
                </button>
                <div
                  className="px-3 py-2 rounded-lg text-white/80 text-xs"
                  style={preview.tagBgStyle}
                >
                  Tag
                </div>
              </div>
            </div>
          </div>

          {/* Theme Name & Icon */}
          <div className="grid grid-cols-[1fr_auto] gap-4">
            <div>
              <label className="text-white text-sm mb-2 block">Theme Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Neon Nights"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                maxLength={24}
                data-testid="theme-name-input"
              />
            </div>
            <div>
              <label className="text-white text-sm mb-2 block">Icon</label>
              <div className="flex gap-1 flex-wrap max-w-[200px]">
                {EMOJI_OPTIONS.map(e => (
                  <button
                    key={e}
                    onClick={() => setIcon(e)}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg transition-all ${
                      icon === e ? 'bg-white/20 ring-2 ring-white/40 scale-110' : 'bg-white/5 hover:bg-white/10'
                    }`}
                    data-testid={`emoji-${e}`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Color Pickers */}
          <div>
            <label className="text-white text-sm mb-3 block">Background Gradient</label>
            <div className="space-y-3 bg-white/5 rounded-lg p-4 border border-white/10">
              <ColorPicker label="Start" value={gradientStart} onChange={setGradientStart} />
              <ColorPicker label="Middle" value={gradientMid} onChange={setGradientMid} />
              <ColorPicker label="End" value={gradientEnd} onChange={setGradientEnd} />
              {/* Gradient preview strip */}
              <div
                className="h-4 rounded-full mt-2"
                style={{ background: `linear-gradient(to right, ${gradientStart}, ${gradientMid}, ${gradientEnd})` }}
              />
            </div>
          </div>

          <div>
            <label className="text-white text-sm mb-3 block">Accent Color</label>
            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <ColorPicker label="Accent" value={accent} onChange={setAccent} />
              <p className="text-white/40 text-xs mt-2">Used for card borders, buttons, tags, and header text gradient</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <Button
              onClick={handleSave}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              data-testid="theme-save-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              {isEditing ? 'Update Theme' : 'Save Theme'}
            </Button>
            {isEditing && (
              <Button
                onClick={handleDelete}
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                data-testid="theme-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ThemeBuilderModal;
